import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check, ArrowRight, Crown, MessageCircle, X, Sparkles, CreditCard, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import wzLogo from "../assets/workzone-logo.png";
import { PeriodToggle, BankTransferDialog, MyBankRequests, periodPrice } from "../components/BillingBank";
import { Landmark } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ORDER = { starter: 1, business: 2, professional: 3, enterprise: 4 };

export default function PricingPage() {
  const navigate = useNavigate();
  const { user, subscription } = useAuth();
  const [plans, setPlans] = useState([]);
  const [payingPlan, setPayingPlan] = useState(null);
  const currentPlan = subscription?.plan || null;
  const currentRank = currentPlan ? ORDER[currentPlan] : 0;
  const waNum = subscription?.support_whatsapp || "";
  const [opts, setOpts] = useState({ periods: [{ months: 1, label: "شهري", discount: 0 }], bank: {} });
  const [period, setPeriod] = useState({ months: 1, label: "شهري", discount: 0 });
  const [bankPlan, setBankPlan] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => { api.billingOptions().then(setOpts).catch(() => {}); }, []);

  useEffect(() => {
    axios.get(`${API}/plans`).then((r) => setPlans(Array.isArray(r.data) ? r.data : (r.data.plans || []))).catch(() => {});
  }, []);

  const waLink = (p) =>
    `https://wa.me/${waNum}?text=${encodeURIComponent(`مرحباً، أرغب بالاشتراك/الترقية إلى باقة ${p.name_ar} في WorkZone`)}`;

  const subscribe = async (p) => {
    if (!user) {
      localStorage.setItem("wz_pending_plan", p.id);
      const redirectUrl = window.location.origin + "/";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      return;
    }
    setPayingPlan(p.id);
    try {
      const data = await api.paymobSubscribe(p.id);
      window.location.assign(data.checkout_url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر بدء الدفع، حاول مرة أخرى");
      setPayingPlan(null);
    }
  };

  const PayButton = ({ p }) => (
    <Button data-testid={`pricing-subscribe-${p.id}`} disabled={payingPlan === p.id} onClick={() => subscribe(p)}
      className="w-full h-11 gap-1.5 bg-blue-600 hover:bg-blue-700">
      {payingPlan === p.id ? <><Loader2 className="h-4 w-4 animate-spin" /> جارٍ فتح الدفع…</> : <><CreditCard className="h-4 w-4" /> اشترك الآن — {p.price} {p.currency}</>}
    </Button>
  );

  return (
    <div dir="rtl" className="min-h-screen bg-gradient-to-b from-slate-50 to-white font-body">
      <header className="sticky top-0 z-10 bg-white/80 backdrop-blur border-b border-slate-100">
        <div className="mx-auto max-w-6xl px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={wzLogo} alt="WorkZone" className="h-9 w-9 rounded-xl object-contain bg-white" />
            <span className="font-heading font-bold text-slate-800">باقات WorkZone</span>
          </div>
          <Button variant="ghost" data-testid="pricing-back-button" onClick={() => navigate("/")} className="gap-1.5 text-slate-600">
            <X className="h-4 w-4" /> رجوع للوحة
          </Button>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-4 py-10">
        <div className="text-center mb-10">
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-slate-900">اختر الباقة المناسبة لشركتك</h1>
          <p className="text-slate-500 mt-3 text-base max-w-xl mx-auto">قارن المزايا وارقِّ باقتك في أي وقت. الترقية فورية وتُفعّل جميع مزايا الباقة مباشرةً.</p>
          {currentPlan && (
            <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-1.5 text-sm font-semibold">
              <Sparkles className="h-4 w-4" /> باقتك الحالية: {subscription?.plan_details?.name_ar || currentPlan}
            </div>
          )}
          <div className="mt-6"><PeriodToggle periods={opts.periods} value={period} onChange={setPeriod} /></div>
        </div>
        {user && <MyBankRequests tick={tick} />}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 items-stretch">
          {plans.map((p) => {
            const isCurrent = p.id === currentPlan;
            const isPopular = p.id === "business";
            const rank = ORDER[p.id] || 0;
            const isDowngrade = currentRank > 0 && rank < currentRank;
            return (
              <div key={p.id} data-testid={`pricing-card-${p.id}`}
                className={`relative rounded-2xl border bg-white p-6 flex flex-col transition ${isCurrent ? "border-emerald-400 ring-2 ring-emerald-400/30" : isPopular ? "border-blue-400 ring-2 ring-blue-400/30 shadow-lg" : "border-slate-200"}`}>
                {isPopular && !isCurrent && (
                  <span className="absolute -top-3 right-6 rounded-full bg-blue-600 text-white text-xs font-bold px-3 py-1">الأكثر شيوعاً</span>
                )}
                {isCurrent && (
                  <span className="absolute -top-3 right-6 rounded-full bg-emerald-600 text-white text-xs font-bold px-3 py-1 inline-flex items-center gap-1">
                    <Check className="h-3 w-3" /> باقتك
                  </span>
                )}
                <div className="flex items-center gap-2">
                  {p.id === "enterprise" && <Crown className="h-5 w-5 text-amber-500" />}
                  <h3 className="font-heading text-xl font-bold text-slate-900">{p.name_ar}</h3>
                </div>
                <div className="mt-3 flex items-end gap-1.5">
                  {p.price != null ? (
                    <div data-testid={`pricing-price-${p.id}`}>
                      <span className="text-3xl font-extrabold text-slate-900">{periodPrice(p.price, period).monthly}</span>
                      <span className="text-slate-500 text-sm mb-1"> {p.currency} / شهرياً</span>
                      {period.months > 1 && <p className="text-xs text-slate-500">{periodPrice(p.price, period).total} {p.currency} لكل {period.label} · <b className="text-emerald-600">توفير {periodPrice(p.price, period).saving}</b></p>}
                    </div>
                  ) : (
                    <span className="text-2xl font-extrabold text-slate-900">{p.period}</span>
                  )}
                </div>
                {p.original_price && (
                  <div className="text-xs text-slate-400 line-through mt-0.5">{p.original_price} {p.currency}</div>
                )}

                {p.employee_limit ? <p className="mt-2 inline-block rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-bold text-slate-700" data-testid={`pricing-limit-${p.id}`}>حتى {p.employee_limit} موظف / مندوب</p> : null}
                <ul className="mt-5 space-y-2 flex-1">
                  {(p.features || []).map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                      <Check className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" /> <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-6">
                  {isCurrent ? (
                    <Button disabled className="w-full bg-emerald-100 text-emerald-700 hover:bg-emerald-100">باقتك الحالية</Button>
                  ) : p.price != null && !isDowngrade ? (
                    <>
                      {period.months === 1 && <PayButton p={p} />}
                      {user && <Button variant="outline" onClick={() => setBankPlan(p)} className="mt-2 w-full gap-1.5" data-testid={`pricing-bank-${p.id}`}><Landmark className="h-4 w-4" /> تحويل بنكي{period.months > 1 ? ` — ${periodPrice(p.price, period).total} ${p.currency}` : ""}</Button>}
                      <a href={waLink(p)} target="_blank" rel="noreferrer" data-testid={`pricing-cta-${p.id}`}
                        className="mt-2 flex items-center justify-center gap-1.5 text-xs text-emerald-600 hover:text-emerald-700">
                        <MessageCircle className="h-3.5 w-3.5" /> أو الاشتراك عبر واتساب
                      </a>
                    </>
                  ) : isDowngrade ? (
                    <a href={waLink(p)} target="_blank" rel="noreferrer" data-testid={`pricing-cta-${p.id}`}>
                      <Button variant="outline" className="w-full">التغيير لهذه الباقة</Button>
                    </a>
                  ) : (
                    <a href={waLink(p)} target="_blank" rel="noreferrer" data-testid={`pricing-cta-${p.id}`}>
                      <Button className={`w-full gap-1.5 ${isPopular ? "bg-blue-600 hover:bg-blue-700" : "bg-slate-900 hover:bg-slate-800"}`}>
                        {p.id === "enterprise" ? "تواصل مع المبيعات" : "الترقية لهذه الباقة"} <ArrowRight className="h-4 w-4 rotate-180" />
                      </Button>
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {bankPlan && <BankTransferDialog plan={bankPlan} period={period} bank={opts.bank || {}} onClose={() => setBankPlan(null)} onDone={() => { setBankPlan(null); setTick((t) => t + 1); }} />}
        {waNum && (
          <div className="text-center mt-10">
            <a href={`https://wa.me/${waNum}`} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-medium">
              <MessageCircle className="h-4 w-4" /> هل لديك سؤال؟ تواصل معنا على واتساب {waNum}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
